import * as React from 'react';
import styled from 'styled-components';
import { ZakekeEnvironment } from '../../environment';
import { useInternalState } from '../../state/provider';

const DebugPanelContainer = styled.div<{ $hideCursor: boolean }>`
	position: fixed;
	min-width: 120px;
	bottom: 0;
	right: 0;
	background: #fff;
	padding: 10px 20px;
	border: 1px solid #ccc;
	z-index: 10000;
	cursor: ${(props) => (props.$hideCursor ? 'auto' : 'pointer')};
`;

const DebugContainer = styled.div`
	position: relative;
	padding: 25px 10px 10px;
	display: flex;
	flex-direction: column;
	gap: 10px;
	overflow: auto;
	max-height: 400px;
`;
const DebugPanelTitle = styled.h3`
	position: absolute;
	top: 5px;
	left: 10px;
	margin: 0;
`;

const DebugPanelClose = styled.button`
	position: absolute;
	top: 5px;
	right: 10px;
	margin: 0;
	padding: 0;
	background: none;
	border: none;
	cursor: pointer;
	z-index: 10002;
`;

const DebugMeshTitle = styled.h4`
	margin: 0;
`;
const DebugMeshList = styled.ul`
	list-style: none;
	padding: 0;
	overflow: auto;
	max-height: 200px;
`;

const DebugTextArea = styled.textarea`
	min-width: 400px;
	height: 200px;
`;

export const DebugPanel: React.FunctionComponent = ({}) => {
	const [show, setShow] = React.useState(false);
	const internalState = useInternalState();
	const stateRef = React.useRef(internalState);

	const env = stateRef.current.state.environment as ZakekeEnvironment;
	const viewer = stateRef.current.state.sceneViewer;
	const [sceneConfig, setSceneConfig] = React.useState<string>('');
	const handleShow = () => {
		setShow(!show);
	};

	const showIfHidden = () => {
		if (!show) {
			setShow(true);
		}
	};

	const downloadFile = (blob: Blob, filename: string) => {
		const url = window.URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = url;
		a.download = filename;
		a.click();
	};

	const downloadScene = async () => {
		console.log('Download Scene');
		const blob = (await env.exportSceneToGlb()) as Blob;
		downloadFile(blob, 'scene.glb');
		console.log('Scene downloaded');
	};

	const downloadARGLB = async () => {
		console.log('Download TryOn GLB');
		const hash = (+new Date()).toString(36);
		const result = await viewer?.exportSceneToARAsync(hash, hash);
		if (!result) {
			alert('Error exporting scene to AR GLB');
			return;
		}
		for (const format of result) {
			const blob = await (await fetch(format.arFileUrl)).blob();
			downloadFile(blob, `scene-ar_file.${format.format}`);
			console.log(`AR GLB downloaded: ${format.format}`);
		}
	};

	const downloadTryOnGLB = async () => {
		console.log('Download TryOn GLB');
		const blob = (await env.exportTryOnMeshToGlb()) as Blob;
		downloadFile(blob, 'tryon.glb');
		console.log('TryOn GLB downloaded');
	};

	React.useEffect(() => {
		const sceneConfig = viewer?.zkScene?.getSceneConfig()?.toJSON();
		if (sceneConfig) {
			setSceneConfig(JSON.stringify(sceneConfig, null, 2));
		}
	}, [viewer]);

	return (
		<DebugPanelContainer $hideCursor={show} onClick={showIfHidden}>
			<DebugPanelTitle>Debug Panel</DebugPanelTitle>
			{show && <DebugPanelClose onClick={handleShow}>X</DebugPanelClose>}
			{show && (
				<DebugContainer>
					<button onClick={downloadScene}>Download Scene</button>
					<button onClick={downloadTryOnGLB}>Download TryOn GLB</button>
					<button onClick={downloadARGLB}>Download AR GLB</button>
					<DebugContainer>
						<DebugMeshTitle>Mesh list</DebugMeshTitle>
						<DebugMeshList>
							{viewer?.scene?.meshes.map((mesh) => (
								<li key={mesh.id}>
									<button
										onClick={() => {
											mesh.visibility = 1;
										}}
									>
										Show
									</button>
									<button
										onClick={() => {
											mesh.visibility = 0;
										}}
									>
										Hide
									</button>
									<span>{mesh.name}</span>
								</li>
							))}
						</DebugMeshList>
						<button
							onClick={() => {
								console.log(viewer?.scene?.meshes);
							}}
						>
							Log Meshes
						</button>
						<button
							onClick={() => {
								console.log(viewer?.scene?.materials);
							}}
						>
							Log Materials
						</button>
						<button
							onClick={() => {
								console.log(viewer?.scene?.textures);
							}}
						>
							Log Textures
						</button>
					</DebugContainer>
					<DebugContainer>
						<DebugMeshTitle>Scene config</DebugMeshTitle>
						<DebugTextArea value={sceneConfig} onChange={(e) => setSceneConfig(e.target.value)}></DebugTextArea>
						<button
							onClick={() => {
								console.log(viewer?.zkScene?.getSceneConfig()?.toJSON());
							}}
						>
							Log Scene Config
						</button>
						<button
							onClick={() => {
								viewer?.loadSceneConfig(JSON.parse(sceneConfig));
								viewer?.setRenderingNeeded();
								viewer?.refreshRendering();
							}}
						>
							Apply Scene Config
						</button>
					</DebugContainer>
				</DebugContainer>
			)}
		</DebugPanelContainer>
	);
};
